import { createGlobalStyle } from 'styled-components'

const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :root {
    --color-primary: #2563eb;
    --color-secondary: #1e40af;
    --color-accent: #3b82f6;
    --color-success: #10b981;
    --color-warning: #f59e0b;
    --color-danger: #ef4444;
    
    --color-bg: #0f172a;
    --color-bg-alt: #1e293b;
    --color-bg-dark: #0f172a;
    --color-bg-gradient: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
    --color-bg-subtle: #1e293b;
    --color-bg-card: #1e293b;
    --color-bg-card-hover: #334155;
    
    --color-text: #f8fafc;
    --color-text-light: #cbd5e1;
    --color-text-white: #ffffff;
    
    --color-border: #334155;
    --color-border-light: #475569;
    
    --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    --gradient-secondary: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    --gradient-accent: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    --gradient-dark: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    --gradient-card: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    --gradient-hero: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #2563eb 100%);
    
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
    --shadow-inner: inset 0 2px 4px 0 rgb(0 0 0 / 0.06);
    
    --border-radius: 0.5rem;
    --border-radius-lg: 1rem;
    --border-radius-xl: 1.5rem;
    
    --container-max-width: 1200px;
    --container-padding: 1rem;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    line-height: 1.6;
    color: var(--color-text);
    background: var(--color-bg-gradient);
    min-height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 0.5em;
    color: var(--color-text);
  }

  h1 {
    font-size: 2.5rem;
    font-weight: 800;
    letter-spacing: -0.025em;
  }

  h2 {
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.025em;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
  }

  h4 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    margin-bottom: 1rem;
    color: var(--color-text-light);
  }

  a {
    color: var(--color-primary);
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-secondary);
    }
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
    font-family: inherit;
    transition: all 0.2s ease;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .container {
    max-width: var(--container-max-width);
    margin: 0 auto;
    padding: 0 var(--container-padding);
  }

  .section {
    padding: 4rem 0;
  }

  .section-lg {
    padding: 6rem 0;
  }

  .text-center {
    text-align: center;
  }

  .text-primary {
    color: var(--color-primary);
  }

  .text-white {
    color: var(--color-text-white);
  }

  .bg-primary {
    background-color: var(--color-primary);
  }

  .bg-alt {
    background-color: var(--color-bg-alt);
  }

  .bg-dark {
    background-color: var(--color-bg-dark);
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.875rem 2rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 50px;
    transition: all 0.3s ease;
    text-decoration: none;
    cursor: pointer;
    border: none;
    position: relative;
    overflow: hidden;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .btn-primary {
    background: var(--gradient-accent);
    color: var(--color-text-white);
    box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
    
    &:hover {
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
      color: var(--color-text-white);
    }

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      transform: translate(-50%, -50%);
      transition: width 0.6s, height 0.6s;
    }

    &:hover::before {
      width: 300px;
      height: 300px;
    }
  }

  .btn-secondary {
    background: rgba(37, 99, 235, 0.1);
    color: var(--color-primary);
    border: 2px solid transparent;
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(37, 99, 235, 0.2);
      border-color: var(--color-primary);
      color: var(--color-primary);
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.2);
    }
  }

  .btn-white {
    background: rgba(255, 255, 255, 0.95);
    color: var(--color-primary);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      color: var(--color-secondary);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
  }

  .btn-lg {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  .card {
    background-color: var(--color-bg);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-md);
    padding: 2rem;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-xl);
    }
  }

  .grid {
    display: grid;
    gap: 2rem;
  }

  .grid-2 {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }

  .grid-3 {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .grid-4 {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  @media (max-width: 768px) {
    :root {
      --container-padding: 1rem;
    }

    h1 {
      font-size: 2rem;
    }

    h2 {
      font-size: 1.75rem;
    }

    .section {
      padding: 3rem 0;
    }

    .section-lg {
      padding: 4rem 0;
    }

    .grid-2,
    .grid-3,
    .grid-4 {
      grid-template-columns: 1fr;
    }

    .btn {
      padding: 0.75rem 1.5rem;
      font-size: 0.95rem;
      min-width: 120px;
    }

    .btn-lg {
      padding: 1rem 2rem;
      font-size: 1.125rem;
      min-width: 140px;
    }
  }

  @media (max-width: 480px) {
    :root {
      --container-padding: 0.75rem;
    }

    h1 {
      font-size: 1.75rem;
    }

    h2 {
      font-size: 1.5rem;
    }

    h3 {
      font-size: 1.25rem;
    }

    .btn {
      padding: 0.625rem 1.25rem;
      font-size: 0.875rem;
      min-width: 100px;
      width: 100%;
      max-width: 280px;
    }

    .btn-lg {
      padding: 0.875rem 1.75rem;
      font-size: 1rem;
      width: 100%;
      max-width: 300px;
    }

    .card {
      padding: 1.5rem;
    }

    .section {
      padding: 2rem 0;
    }

    .section-lg {
      padding: 3rem 0;
    }

    .grid {
      gap: 1.5rem;
    }

    .btn + .btn {
      margin-top: 1rem;
    }
  }
`

export default GlobalStyles 